import { useState } from "react";

export type AiDataQualityImpact =
  | "no_material_issues"
  | "limited_impact"
  | "material_impact"
  | "analysis_blocked";

export type AiDataQualityIssue = {
  issue?: string | null;
  effect_on_analysis?: string | null;
  required_action?: string | null;
};

export type AiDataQualityMetric = { label: string; value: string };

export type AiDataQualitySummary = {
  impact_on_analysis?: AiDataQualityImpact | null;
  summary?: string | null;
  issues?: AiDataQualityIssue[] | null;
};

const IMPACT_LABELS: Record<AiDataQualityImpact, string> = {
  no_material_issues: "No material issues",
  limited_impact: "Limited impact",
  material_impact: "Material impact",
  analysis_blocked: "Analysis blocked",
};

function AiDataQualityIssueCard({ issue }: { issue: AiDataQualityIssue }) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <article className="global-insights-overlay__data-quality-item">
      <button
        type="button"
        className="global-insights-overlay__data-quality-item-header"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <h5>{issue.issue ?? "Data quality issue"}</h5>
        <span
          className={`global-insights-overlay__data-quality-item-chevron${isExpanded ? " global-insights-overlay__data-quality-item-chevron--expanded" : ""}`}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>
      {isExpanded && (
        <div className="global-insights-overlay__data-quality-item-details">
          {issue.effect_on_analysis && (
            <p>
              <span>Effect on analysis</span>
              {issue.effect_on_analysis}
            </p>
          )}
          {issue.required_action && (
            <p>
              <span>Required action</span>
              {issue.required_action}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

export type AiDataQualitySectionProps = {
  headingId: string;
  title?: string;
  dataQuality?: AiDataQualitySummary | null;
  metrics?: AiDataQualityMetric[];
  emptyStateDescription?: string;
};

/** Shared "Data quality" section for AI Insights overlays (Planning, Day 1 Readiness, ...). */
export function AiDataQualitySection({
  headingId,
  title = "Data quality",
  dataQuality,
  metrics = [],
  emptyStateDescription = "The available data supports the current analysis.",
}: AiDataQualitySectionProps) {
  const dataQualityIssues = Array.isArray(dataQuality?.issues) ? dataQuality.issues.slice(0, 3) : [];
  const dataQualityLabel = IMPACT_LABELS[dataQuality?.impact_on_analysis ?? "no_material_issues"];

  return (
    <section className="global-insights-overlay__section" aria-labelledby={headingId}>
      <h3 id={headingId} className="global-insights-overlay__section-title global-insights-overlay__section-title--raid">
        {title}
      </h3>
      <div
        className={`global-insights-overlay__data-quality global-insights-overlay__data-quality--${dataQuality?.impact_on_analysis ?? "no_material_issues"}`}
      >
        {dataQuality == null ? (
          <div className="global-insights-overlay__data-quality-empty">
            <strong>No data quality issues reported</strong>
            <span>{emptyStateDescription}</span>
          </div>
        ) : (
          <>
            <div className="global-insights-overlay__data-quality-impact">
              <span className="global-insights-overlay__data-quality-impact-key">Impact:</span>
              <span className="global-insights-overlay__data-quality-label">{dataQualityLabel}</span>
            </div>
            {dataQuality.summary && (
              <p className="global-insights-overlay__data-quality-summary">{dataQuality.summary}</p>
            )}
            {metrics.length > 0 && (
              <div className="global-insights-overlay__data-quality-metrics" aria-label="Data quality context">
                {metrics.map((metric) => (
                  <div className="global-insights-overlay__data-quality-metric" key={metric.label}>
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                  </div>
                ))}
              </div>
            )}
            {dataQualityIssues.length > 0 && (
              <div className="global-insights-overlay__data-quality-issues">
                <h4 className="global-insights-overlay__data-quality-subtitle">Reported issues</h4>
                <div className="global-insights-overlay__data-quality-list">
                  {dataQualityIssues.map((issue, index) => (
                    <AiDataQualityIssueCard key={`${issue.issue ?? "issue"}-${index}`} issue={issue} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
